import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

// Geometry only: the frame owns its composer and never shares its contents.
export default function useAssistantViewport(visible: boolean) {
  const resting = useRef<{ width: number; height: number; keyboard: boolean } | null>(null);
  const [viewport, setViewport] = useState<{
    top: number; left: number; width: number; height: number; keyboard: boolean;
  } | null>(null);
  useEffect(() => {
    const source = window.visualViewport;
    let settled: ReturnType<typeof setTimeout>;
    let animation = 0;
    const update = () => {
      const width = window.innerWidth;
      const phone = width <= 720 || (width <= 1024 && window.matchMedia('(pointer: coarse)').matches);
      if (!phone) {
        resting.current = null;
        setViewport(null);
        return;
      }
      const scale = source?.scale || 1;
      const height = Math.min(source?.height ?? window.innerHeight, window.innerHeight / scale);
      if (!Number.isFinite(height) || height <= 0) return;
      // Comparing both viewports misses Android's resize-content keyboard.
      // Remember the unobscured height, including while the dock is minimized.
      const previous = resting.current;
      if (!previous || previous.width !== width) {
        resting.current = { width, height: Math.max(window.innerHeight,
          previous?.keyboard ? previous.width : height * scale), keyboard: false };
      }
      const baseline = resting.current!;
      const active = document.activeElement;
      // An iframe is an opaque focus boundary; never inspect its input or draft.
      const editing = active?.tagName === 'IFRAME'
        || !!active?.matches('input, textarea, [contenteditable="true"]');
      const available = height * scale;
      const lost = baseline.height - available;
      const keyboard = window.innerHeight - available > 120 || ((editing || baseline.keyboard) && lost > 120);
      if (scale !== 1 && !keyboard) {
        setViewport(null); // Pinch zoom alone is not a keyboard opening.
        return;
      }
      if (!keyboard) {
        baseline.height = !editing && lost > 120
          ? window.innerHeight : Math.max(baseline.height, window.innerHeight, available);
      }
      baseline.keyboard = keyboard;
      const next = visible ? { top: Math.max(0, source?.offsetTop ?? 0),
        left: Math.max(0, source?.offsetLeft ?? 0),
        width: Math.min(source?.width ?? width, width / scale), height, keyboard } : null;
      setViewport(current => current?.top === next?.top && current?.left === next?.left
        && current?.width === next?.width && current?.height === next?.height
        && current?.keyboard === next?.keyboard ? current : next);
    };
    const changed = () => {
      cancelAnimationFrame(animation);
      animation = requestAnimationFrame(update);
      // Some phone keyboards finish their animation after the last event.
      clearTimeout(settled);
      settled = setTimeout(update, 150);
    };
    update();
    source?.addEventListener('resize', changed);
    source?.addEventListener('scroll', changed);
    window.addEventListener('resize', changed);
    window.addEventListener('orientationchange', changed);
    window.addEventListener('blur', changed);
    document.addEventListener('focusin', changed);
    return () => {
      cancelAnimationFrame(animation);
      clearTimeout(settled);
      source?.removeEventListener('resize', changed);
      source?.removeEventListener('scroll', changed);
      window.removeEventListener('resize', changed);
      window.removeEventListener('orientationchange', changed);
      window.removeEventListener('blur', changed);
      document.removeEventListener('focusin', changed);
    };
  }, [visible]);
  return {
    keyboard: visible && viewport?.keyboard,
    style: (visible && viewport ? {
      '--assistant-viewport-top': `${viewport.top}px`,
      '--assistant-viewport-left': `${viewport.left}px`,
      '--assistant-viewport-width': `${viewport.width}px`,
      '--assistant-viewport-height': `${viewport.height}px`,
    } : undefined) as CSSProperties | undefined,
  };
}
